import type { ExercisePerformance, WorkoutSession } from "./types";

const exportedAtFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" });
const sessionDateFormat = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
const numberFormat = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
const oneLine = (value: string) => value.replace(/\s+/g, " ").trim();

function prescriptionLine(performance: ExercisePerformance) {
  const prescription = performance.prescriptionSnapshot;
  const sets = prescription.minSetCount != null && prescription.minSetCount !== prescription.setCount
    ? `${prescription.minSetCount}–${prescription.setCount}`
    : String(prescription.setCount);
  const range = prescription.trackingMode === "duration"
    ? `${prescription.minDuration}–${prescription.maxDuration} secondes`
    : `${prescription.minReps}–${prescription.maxReps} répétitions${prescription.perSide ? " par côté" : ""}`;
  return `${sets} séries × ${range}`;
}

export function historyToMarkdown(sessions: WorkoutSession[], exportedAt = Date.now()) {
  const historical = sessions
    .filter((session) => session.status !== "active")
    .sort((left, right) => right.startedAt - left.startedAt);
  const completedSetCount = historical.reduce((total, session) => total + session.performances.reduce((sum, performance) => sum + performance.sets.filter((set) => set.completedAt).length, 0), 0);
  const lines = [
    "# Historique d’entraînement Forge",
    "",
    `Exporté le ${exportedAtFormat.format(exportedAt)}.`,
    "",
    "> Ce fichier est conçu pour être transmis à un agent afin d’analyser la progression, la récupération et les ajustements du programme. Les objectifs et les performances réalisées sont distingués.",
    "",
    "## Résumé",
    "",
    `- ${historical.length} séance${historical.length !== 1 ? "s" : ""}`,
    `- ${completedSetCount} série${completedSetCount !== 1 ? "s" : ""} réalisée${completedSetCount !== 1 ? "s" : ""}`,
  ];

  for (const session of historical) {
    const durationMinutes = Math.max(0, Math.round(((session.completedAt ?? session.startedAt) - session.startedAt) / 60_000));
    lines.push(
      "",
      `## ${sessionDateFormat.format(session.startedAt)} — ${oneLine(session.templateNameSnapshot)}`,
      "",
      `- Statut : ${session.status === "completed" ? "terminée" : "abandonnée"}`,
      `- Durée : ${durationMinutes} min`,
    );
    if (session.notes?.trim()) lines.push(`- Note de séance : ${oneLine(session.notes)}`);

    for (const performance of session.performances) {
      const completedSets = performance.sets.filter((set) => set.completedAt);
      lines.push("", `### ${oneLine(performance.exerciseNameSnapshot)}`, "", `- Objectif : ${prescriptionLine(performance)}`);
      if (performance.notes?.trim()) lines.push(`- Note : ${oneLine(performance.notes)}`);
      if (!completedSets.length) {
        lines.push("- Performance : aucune série réalisée");
        continue;
      }
      for (const set of completedSets) {
        const result = performance.prescriptionSnapshot.trackingMode === "duration"
          ? `${set.duration ?? 0} secondes`
          : `${set.reps ?? 0} répétitions${performance.prescriptionSnapshot.perSide ? " par côté" : ""}`;
        lines.push(`- Série ${set.setNumber} : ${result} | ${numberFormat.format(set.load)} ${set.unit}${set.rir != null ? ` | RIR ${set.rir}` : ""}`);
      }
    }
  }

  if (!historical.length) lines.push("", "Aucune séance n’est encore disponible dans l’historique.");
  return `${lines.join("\n")}\n`;
}

export function downloadMarkdown(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
